import React, { useEffect, useState } from 'react';
import { Divider } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import Dialog from 'components/modals/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import PropTypes from 'prop-types';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import { secondary1000, secondary300 } from '../../../src/themes/colors';
import Button from '../../../src/components/buttons/Button';

const styles = theme => ({
  DialogTitleRoot: {
    display: 'flex',
    alignItems: 'center',
    color: secondary1000,
    fontSize: '1rem !important',
    fontWeight: '600 !important',
    letterSpacing: '-0.013rem',
    padding: 24,
  },
  DialogContentRoot: {
    padding: '1.5rem',
    overflow: 'visible',
  },
  DialogContentTextRoot: {
    fontSize: '0.875rem',
    color: `${secondary1000} !important`,
    fontWeight: 400,
    margin: '1.5rem 0',
    lineHeight: '1.25rem',
  },
  DialogActionsRoot: {
    padding: '1.5rem',
  },
  closeIcon: {
    position: 'absolute',
    right: '1.875rem',
    width: '1.375rem',
    color: secondary300,
    cursor: 'pointer',
  },
  dividerRoot: {
    backgroundColor: theme.palette.secondary['100'],
  },
});

const ConfirmationModal = (props) => {
  const {
    title,
    onCancelClick,
    onConfirmClick,
    cancelLabel,
    confirmLabel,
    confirmBtnColor,
    showConfirm,
    showCancel,
    classes,
    size,
    confirmDisabled,
    onBackdropClick,
    open,
    showDialogActions,
    divider,
    classNames,
  } = props;
  const [loading, setLoading] = useState(false);

  const handleOnConfirmClick = async (e) => {
    setLoading(true);
    await onConfirmClick(e);
  };

  useEffect(() => {
    if (open) {
      setLoading(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      size={size}
      onBackdropClick={onBackdropClick}
      aria-labelledby="dialog-title"
      withPadding={false}
      style={{ zIndex: 1400 }}
    >
      <DialogTitle
        id="dialog-title"
        classes={{ root: classes.DialogTitleRoot }}
        className={classNames.title}
        disableTypography
      >
        <div>{title}</div>
        <CloseRoundedIcon onClick={onCancelClick} classes={{ root: classes.closeIcon }} />
      </DialogTitle>
      {divider && <Divider classes={{ root: classes.dividerRoot }} />}
      <DialogContent classes={{ root: classes.DialogContentRoot }}>
        <DialogContentText classes={{ root: classes.DialogContentTextRoot }} className={classNames.content}>
          {props.children}
        </DialogContentText>
      </DialogContent>
      {divider && <Divider classes={{ root: classes.dividerRoot }} />}
      {showDialogActions &&
      <DialogActions classes={{ root: classes.DialogActionsRoot }}>
        {showCancel && (
          <Button autoFocus onClick={onCancelClick} color="secondary" variant="text" size="medium" disabled={loading}>
            {cancelLabel}
          </Button>
        )}
        {showConfirm &&
          <Button
            className="ml-2"
            autoFocus
            onClick={handleOnConfirmClick}
            color={confirmBtnColor}
            variant="contained"
            size="medium"
            disabled={confirmDisabled}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        }
      </DialogActions>}
    </Dialog>
  );
};

ConfirmationModal.propTypes = {
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  showConfirm: PropTypes.bool,
  showCancel: PropTypes.bool,
  showDialogActions: PropTypes.bool,
  confirmBtnColor: PropTypes.string,
  classes: PropTypes.element.isRequired,
  size: PropTypes.string,
  title: PropTypes.string,
  onCancelClick: PropTypes.func,
  onConfirmClick: PropTypes.func,
  onBackdropClick: PropTypes.func,
  children: PropTypes.element.isRequired,
  open: PropTypes.bool.isRequired,
  divider: PropTypes.bool,
  classNames: PropTypes.shape({}),
};

ConfirmationModal.defaultProps = {
  cancelLabel: 'Cancel',
  confirmLabel: 'Confirm',
  confirmDisabled: false,
  showConfirm: true,
  showCancel: true,
  showDialogActions: true,
  confirmBtnColor: 'accent3',
  size: 'small',
  title: 'Confirm',
  divider: true,
  classNames: {},
  onCancelClick: () => {},
  onConfirmClick: () => {},
  onBackdropClick: () => {},
};

export default withStyles(styles)(ConfirmationModal);
